import { yupResolver } from "@hookform/resolvers/yup";
import { db } from "@utils/IndexedDb";
import { toUnicodeLocale } from "@utils/string";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import React, { Dispatch, FC, Fragment, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { UseFormSetValue, UseFormWatch, useForm } from "react-hook-form";

import FilterAction from "@/components/ui/Form/Filters/FilterAction";
import "@/components/ui/Form/Filters/FilterBar.css";
import FilterComponent from "@/components/ui/Form/Filters/FilterComponent";
import FilterLabel from "@/components/ui/Form/Filters/FilterLabel";
import FilterPanel from "@/components/ui/Form/Filters/FilterPanel";
import {
  SEARCH_BAR_AS_PER_PAGE,
  SEARCH_PAGE,
  SEO_SALE_PAGE,
  cleanParams,
  parseFilters,
  queryStringToParams,
} from "@/components/ui/Form/Filters/FilterUtils";
import { SearchFormSchema, SearchFormSchemaType } from "@/components/ui/Form/Filters/common";
import Autocomplete from "@/components/ui/Form/Filters/components/Autocomplete/Autocomplete";
import { locationLabelFromCategories, locationsFromID } from "@/components/ui/Form/Filters/components/Autocomplete/AutocompleteUtils";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";
import { clearAllTrackingEvent, showAllUnitsClick } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { PRODUCT_TYPE, SearchParams, VISUAL_VARIANT } from "@/types";

interface Props {
  name: string;
  variant: VISUAL_VARIANT;
  filters: string[];
  isAutoGeneratedCarousel?: boolean;
}

const FilterBar: FC<Props> = ({ filters, name, variant, isAutoGeneratedCarousel = false }) => {
  const initialized = useRef<boolean>();
  const currentFilterContainer = useRef<HTMLDivElement[]>([]);
  const currentFilterIndex = useRef<number>(10000);
  const query = useSearchParams();
  const pathname = usePathname();
  const t = useTranslations();
  const locale = useLocale();
  const { isMd } = useBreakPoint("md");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const priceDefault = useRef<number[]>();
  const areaDefault = useRef<number[]>();
  const [currentFilter, setCurrentFilter] = useState<string>("");
  const { currency, measurement } = useContext(UserContext);
  const { setMobilePanelID, setDesktopPanelID, desktopPanelID } = useContext(NavigationContext);
  const {
    params,
    type,
    counterParams,
    counterDispatch,
    counterTotal,
    dispatch,
    isClearAll,
    setIsSearchPageTrachForFilterSelection,
    isRemoveFilter,
    setIsRemoveFilter,
  } = useContext(SearchContext);
  const [locationLabel, setLocationLabel] = useState<string>("");
  const [isClearing, setIsClearing] = useState(false);
  const isSearchPage = desktopPanelID === "search" ? true : pathname?.includes("/search/") || false;
  const { control, register, watch, handleSubmit, reset, setValue, getValues } = useForm<SearchFormSchemaType>({
    resolver: yupResolver(SearchFormSchema),
    defaultValues: {
      currency: currency,
      measurement: measurement,
      locale: toUnicodeLocale(locale),
      // onlineExclusive: "" as any,
    },
  });
  useEffect(() => {
    if (isRemoveFilter === "") return;
    if (isRemoveFilter === "price") {
      resetValue(isRemoveFilter);
      setIsRemoveFilter("");
    }
    setIsRemoveFilter("");
  }, [isRemoveFilter]);

  useEffect(() => {
    initialized.current = true;
    return () => {
      setCurrentFilter("");
    };
  }, []);
  useEffect(() => {
    if (SEARCH_BAR_AS_PER_PAGE.includes(name)) {
      resetHandler([
        "propertyType",
        "bedrooms",
        "bathrooms",
        "price",
        "area",
        "status",
        "onlineExclusive",
        // "isFurnished",
        //"unitViews",
        "features",
        "deliveryDate",
        "lifeStyle",
        "brands",
        "others",
        "offers",
        "vTour",
        "webIE",
      ]);
      name !== SEO_SALE_PAGE && submitHandler && submitHandler();
    }
  }, [currency]);

  useEffect(() => {
    setValue("price", undefined, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [currency]);

  useEffect(() => {
    if (initialized.current) {
      const { unsubscribe } = watch((value) => {
        const payload = { ...value };
        // if (payload.price && payload.price[0] === priceDefault?.current?.[0] && payload.price[1] === priceDefault?.current?.[1]) {
        //   delete payload.price;
        // }
        if (payload.area && payload.area[0] === areaDefault?.current?.[0] && payload.area[1] === areaDefault?.current?.[1]) {
          delete payload.area;
        }
        if (type !== PRODUCT_TYPE.PROJECT) {
          delete payload.onlyAvailable;
        } else {
          !payload.onlyAvailable ? delete payload.onlyAvailable : (payload.onlyAvailable = true);
        }
        counterDispatch({ action: "COUNT", payload: { ...payload, type: type || payload.type } });
      });
      return () => unsubscribe();
    }
  }, [watch, priceDefault, areaDefault, type]);

  useEffect(() => {
    reset({
      currency: currency,
      measurement: measurement,
      locale: toUnicodeLocale(locale),
    });
  }, []);

  // useEffect(() => {
  //   if (!isClearing) {
  //     if (query.size > 0) {
  //       const searchParams = queryStringToParams(query);
  //       reset(searchParams);
  //     } else {
  //       db.PINS.toArray().then((pins) => {
  //         const activePins = pins.map((pin) => {
  //           return { ...pin, active: true };
  //         });
  //         db.PINS.bulkPut(activePins);
  //       });
  //       reset({
  //         currency: currency,
  //         measurement: measurement,
  //         locale: toUnicodeLocale(locale),
  //       });
  //     }
  //   }
  //   // }, [query, isClearing]);
  // }, [query, isClearing]);

  useEffect(() => {
    if (counterParams.category) {
      locationsFromID(counterParams.category as string[]).then((value) => {
        setLocationLabel(
          value.length > 0
            ? `${value[0]}${t("search.label.locationList", { count: value.length, first: value[0] })}`
            : t("search.label.allLocations"),
        );
      });
    } else {
      setLocationLabel(t("search.label.allLocations"));
    }
  }, [counterParams]);

  const handleFilterSelection = async (params: SearchParams) => {
    let location = "All location";
    if (params.category) {
      location = (await locationsFromID(params.category as string[])).join(",");
    }
    showAllUnitsClick(
      location,
      Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
      Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
      Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
    );
  };

  const dbValues = useLiveQuery(async () => {
    if (!isClearing) {
      const dbFilters = await parseFilters(type, filters, currency, measurement);
      if (dbFilters.area) {
        areaDefault.current = [...dbFilters.area];
      }
      if (dbFilters.price) {
        priceDefault.current = [...dbFilters.price];
      }
      return dbFilters;
    }
    return null;
  }, [currency, measurement]);

  const submitHandler = handleSubmit(
    (values) => {
      const payload = { ...values };
      if (payload.price && payload.price[0] === priceDefault?.current?.[0] && payload.price[1] === priceDefault?.current?.[1]) {
        delete payload.price;
      }
      if (payload.area && payload.area[0] === areaDefault?.current?.[0] && payload.area[1] === areaDefault?.current?.[1]) {
        delete payload.area;
      }
      if (type !== PRODUCT_TYPE.PROJECT) {
        delete payload.onlyAvailable;
      } else {
        !payload.onlyAvailable ? delete payload.onlyAvailable : (payload.onlyAvailable = true);
      }

      dispatch({ action: "SEARCH", payload: { ...payload, type: type || values.type } });
    },
    (errors) => {
      console.log("errors", errors);
    },
  );

  const resetValue = (key: any) => {
    const currentValue = getValues(key);
    if (!!currentValue || currentValue === true) {
      if (Array.isArray(currentValue)) {
        setValue(key, []);
      } else if (key === "status" || key === "isFurnished") {
        setValue(key, undefined);
      } else if (key === "onlineExclusive" || typeof currentValue === "boolean" || key === "deliveryDate" || typeof currentValue === "string") {
        setValue(key, false);
      } else {
        setValue(key, undefined);
      }
    }
  };

  const resetHandler = (keys: string[]) => {
    setIsClearing(true);
    keys.forEach((key) => resetValue(key));
    setTimeout(() => setIsClearing(false), 1000);
    // setValue("category", []);
  };

  useEffect(() => {
    if (isClearAll) {
      resetHandler([
        "propertyType",
        "bedrooms",
        "bathrooms",
        "price",
        "area",
        "status",
        "onlineExclusive",
        // "isFurnished",
        //"unitViews",
        "features",
        "deliveryDate",
        "lifeStyle",
        "brands",
        "others",
        "offers",
        "vTour",
        "webIE",
      ]);
    }
  }, [isClearAll]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const reference = currentFilterContainer.current[currentFilterIndex.current];
      if (reference && (event.target as Node) !== reference && !reference?.contains(event.target as Node)) {
        setCurrentFilter("");
      }
    };
    document.addEventListener("mouseup", handleClickOutside);
    document.addEventListener("touchend", handleClickOutside);
    return () => {
      document.removeEventListener("mouseup", handleClickOutside);
      document.removeEventListener("touchend", handleClickOutside);
    };
  }, [currentFilterIndex, currentFilterContainer]);

  const handleClearTracking = async (params: SearchParams, searchTerm: string | number) => {
    let location = "All location";
    if (params.category) {
      location = (await locationsFromID(params.category as string[])).join(",");
    }
    clearAllTrackingEvent(
      location,
      Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
      Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
      Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
      searchTerm,
    );
  };

  return (
    <div
      className={`filterbar filterbar--${variant}`}
      onClick={() => {
        setIsSearchPageTrachForFilterSelection(false);
        const pathname = window.location.pathname;
        if (isMd && !pathname.includes("search")) {
          // setDesktopPanelID("search");
        }
      }}
    >
      <form method="GET" action={`/${locale}/search/`} onSubmit={submitHandler} name={name || "no-name"}>
        <input {...register("currency")} type="hidden" />
        <input {...register("measurement")} type="hidden" />
        <input {...register("locale")} type="hidden" />
        {/* <input {...register("onlineExclusive")} type="hidden" /> */}
        <div className="flex items-stretch gap-s">
          <div className="md:hidden w-full">
            {/* {!SEARCH_BAR_AS_PER_PAGE.includes(name) && (
              <button type="button" onClick={() => setMobilePanelID("location")} className="p-xxs flex w-full justify-between items-center px-m">
                <span className={`${VISUAL_VARIANT.DARK ? "text-white" : "text-black"} text-m font-secondary font-secondary-light leading-none`}>
                  {t("search.label.mobile")}
                </span>
                <Icon name="search" size="24" className={`${VISUAL_VARIANT.DARK ? "stroke-white" : "stroke-black"}`} />
              </button>
            )} */}
            {
              <div
                onClick={() => setMobilePanelID("location")}
                className="md:hidden w-full h-full border border-gray-20 rounded-full flex items-center justify-between px-s py-xs"
              >
                <div className="flex items-center gap-xs">
                  <Icon name="geotag" size="24" className={`${isSearchPage ? "stroke-black" : "stroke-white"}`} />
                  <span className={`${isSearchPage ? "" : "text-white"} text-m font-medium`}>{locationLabel}</span>
                </div>
                <button className={`${isSearchPage ? "" : "text-white"} text-s underline font-medium`} type="button">
                  {`+` + t("label.common.add")}
                </button>
              </div>
            }
          </div>
          <div className="filterbar__item focus:bg-white">
            <Autocomplete
              container={name}
              control={control}
              reverse={isSearchPage ? variant === VISUAL_VARIANT.DARK : false}
              name="category"
              label={t("search.label.locations")}
              placeholder={t("search.label.allLocations")}
              isHomePage={isAutoGeneratedCarousel || isSearchPage}
              isAutoGeneratedCarousel={isAutoGeneratedCarousel}
            />
          </div>
          <div className="filterbar__pipe"></div>
          {dbValues &&
            filters.map((key, index) => {
              const filterName = `${name}-filter-${key}`;
              return (
                <Fragment key={`${name}-container-${key}`}>
                  <div
                    className={`filterbar__item px-m ${currentFilter === filterName ? "filterbar__item--selected" : ""}`}
                    style={{ position: "relative" }}
                  >
                    <AnimatePresence>
                      <motion.div
                        transition={{ delay: index * 0.1 }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="h-full"
                      >
                        <FilterLabel
                          reverse={variant === VISUAL_VARIANT.DARK}
                          onClick={() => {
                            setCurrentFilter(currentFilter === filterName ? "" : filterName);
                            currentFilterIndex.current = index;
                          }}
                          asButton={true}
                          label={t(`filter.label.${key}`)}
                          filterName={filterName}
                          currentFilter={currentFilter}
                          filterKey={key}
                        />
                      </motion.div>
                    </AnimatePresence>
                    <AnimatePresence>
                      {currentFilter === filterName && (
                        <motion.div
                          initial={{ opacity: 0, y: -30, x: "-50%" }}
                          exit={{ opacity: 0, y: -30, x: "-50%" }}
                          animate={{ opacity: 1, y: -8, x: "-50%" }}
                          transition={{ duration: 0.3 }}
                          className={`absolute ${isSearchPage || isAutoGeneratedCarousel ? "top-[calc(100%+10px)]" : "bottom-full"} left-1/2 bg-gray-7 rounded-2xl p-m z-20 min-w-full flex items-start flex-col w-max`}
                          style={
                            isSearchPage
                              ? {}
                              : {
                                  transform: "translateX(-50%)",
                                  marginBottom: "8px",
                                }
                          }
                          ref={(el) => {
                            currentFilterContainer.current[index] = el as HTMLDivElement;
                          }}
                        >
                          <FilterComponent
                            key={`${name}-filter-${key}-${currency}-${measurement}`}
                            displayInSearchBar
                            values={dbValues[key]}
                            control={control}
                            name={name}
                            filterKey={key}
                            setValue={setValue}
                            watch={watch}
                          />
                          {SEARCH_BAR_AS_PER_PAGE.includes(name) && (
                            <FilterAction
                              resetHandler={() => {
                                resetValue(key);
                              }}
                              setDisplayFilters={setCurrentFilter as Dispatch<SetStateAction<string | boolean>>}
                              resetSearchFilters={() => {
                                handleClearTracking(counterParams, t("button.label.clearAll"));
                              }}
                            />
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {index < filters.length - 1 && <div className="filterbar__pipe"></div>}
                </Fragment>
              );
            })}
          {!SEARCH_BAR_AS_PER_PAGE.includes(name) && (
            <div className="relative hidden md:block">
              <motion.button
                // animate={controls}
                disabled={counterTotal === 0}
                className={`btn btn-primary-black btn-medium whitespace-nowrap items-center justify-center md:flex md:min-w-[230px]`}
                type="submit"
                onClick={(event) => {
                  event.stopPropagation();
                  setDesktopPanelID("");
                  handleFilterSelection(counterParams);
                }}
              >
                {t("button.label.showAllUnits", { total: Object.keys(cleanParams(counterParams)).length === 0 ? 0 : counterTotal })}
              </motion.button>
            </div>
          )}
          {(SEARCH_BAR_AS_PER_PAGE.includes(name) || (isMobile && name === "hero-first-card")) && (
            <FilterPanel
              name={name}
              control={control}
              setValue={setValue}
              watch={watch}
              isHomepage={!isSearchPage}
              resetHandler={() => {
                resetHandler([
                  "propertyType",
                  "bedrooms",
                  //"bathrooms",
                  "price",
                  "area",
                  "status",
                  "onlineExclusive",
                  // "isFurnished",
                  //"unitViews",
                  "features",
                  "deliveryDate",
                  "lifeStyle",
                  "brands",
                  "others",
                  "offers",
                  "vTour",
                  "webIE",
                ]);
              }}
              submitHandler={submitHandler}
              filters={[
                "propertyType",
                "bedrooms",
                //"bathrooms",
                "price",
                "area",
                "status",
                "onlineExclusive",
                "vTour",
                "webIE",
                // "isFurnished",
                //"unitViews",
                "features",
                "deliveryDate",
                "lifeStyle",
                "brands",
                "others",
                "offers",
              ]}
            />
          )}
        </div>
      </form>
    </div>
  );
};
export default FilterBar;
